import React, { useMemo } from 'react';
import FeedResultCard from '../../components/FeedResultCard';
import { FEED_CHIP } from '../../components/investigation/feedCardStyles';

const CLEAN_SHELL = 'border-emerald-500/30 hover:border-emerald-400/40';
const SIGNAL_SHELL =
  'border-indigo-500/40 bg-indigo-950/20 hover:border-indigo-400/60 shadow-[0_0_10px_rgba(99,102,241,0.08)]';

function recordKey(record, idx) {
  return `${record?.url || record?.title || 'record'}-${idx}`;
}

function buildInspectRow({ records, companyName, companyDomain, count }) {
  const clean = count === 0;
  return {
    ui_bucket: 'legal_courts',
    platform: 'Court & Registry Intelligence',
    title: clean
      ? 'Court & Registry Intelligence — No signals found'
      : `Court & Registry Intelligence — ${count} signal(s)`,
    snippet: clean
      ? 'No attested court, registry, or public-record signals were found for this entity.'
      : `${count} court, registry, or public-record signal(s) from Romania and international sources.`,
    url: records[0]?.url || '',
    domain: companyDomain || '',
    company_name: companyName || '',
    details: {
      records,
      domain: companyDomain || '',
      company_name: companyName || '',
      records_count: count,
    },
    legal_records: records,
  };
}

/**
 * Corporate Court & Registry Intelligence summary card (HIBP-style).
 */
export default function LegalCourtsCard({
  records = [],
  loading = false,
  scanComplete = false,
  companyName = '',
  companyDomain = '',
  onInspect,
}) {
  const legalRecords = useMemo(
    () => (records || []).filter((row) => row?.ui_bucket === 'legal_risk'),
    [records],
  );
  const count = legalRecords.length;
  const clean = scanComplete && count === 0;
  const pending = loading && !scanComplete;

  const inspectRow = useMemo(
    () => buildInspectRow({
      records: legalRecords,
      companyName,
      companyDomain,
      count,
    }),
    [legalRecords, companyName, companyDomain, count],
  );

  const platformLabels = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const row of legalRecords) {
      const label = String(row.platform || 'Legal Portal').trim();
      if (!label || seen.has(label)) continue;
      seen.add(label);
      out.push(label);
    }
    return out;
  }, [legalRecords]);

  if (pending && count === 0) {
    return (
      <section className="space-y-3" aria-label="Court and registry intelligence" data-testid="legal-courts-card-loading">
        <div className="h-16 animate-pulse rounded-2xl border border-indigo-500/20 bg-slate-900/60" aria-busy="true" />
      </section>
    );
  }

  const cardTitle = clean
    ? 'Court & Registry Intelligence — No signals found'
    : `Court & Registry Intelligence — ${count} signal(s)`;

  return (
    <div aria-label="Court and registry intelligence" data-testid="legal-courts-card">
      <FeedResultCard
        res={inspectRow}
        title={cardTitle}
        icon={clean ? '✓' : '⚖️'}
        isAlert={!clean}
        shellClassName={clean ? CLEAN_SHELL : SIGNAL_SHELL}
        onInspect={() => onInspect?.(inspectRow)}
      >
        {companyDomain ? (
          <span className={`${FEED_CHIP} font-mono text-[10px]`}>{companyDomain}</span>
        ) : null}
        {!clean && platformLabels.length > 0 ? (
          <div className="flex flex-wrap gap-1.5">
            {platformLabels.map((label) => (
              <span key={label} className={`${FEED_CHIP} border-indigo-500/30 text-indigo-200`}>
                {label}
              </span>
            ))}
          </div>
        ) : null}
      </FeedResultCard>
    </div>
  );
}

export { buildInspectRow, recordKey };
